import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { applyParticipantTerminology } from "Constants/constant";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import Loader from "components/common/Loader/Loader";
import {
  STRIPE_CHECK_SUBSCRIPTION_STATUS,
  checkSubscriptionStatus,
} from "helpers/RolesPermission";
import useCareLabels from "hooks/useCareLabels";
import { BiExit } from "react-icons/bi";
import { HiOutlineUserGroup } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { authenticatedUser } from "store/actions/auth.actions";
import { getPaymentSettings, getUser } from "store/selector/auth.selector";
import {
  getStripeAttachSubscribeSuccess,
  getStripeGetDefaultPaymentMethodLoader,
  getXeroSubscriptionData,
} from "store/selector/sp.selector";
import api from "store/services/sp.services";
import { STRIPE_SUBSCRIPTION_UPDATED_EVENT } from "pages/Stripe/stripeSubscriptionRefresh";

const SubscriptionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { careSector, participantLabel, participantLabelPlural } = useCareLabels();

  const defaultPaymentMethodLoader = useSelector(
    getStripeGetDefaultPaymentMethodLoader
  );

  const [isFetchCurrentSubs, setIsFetchCurrentSubs] = useState(false);
  const [isCurrentSubscription, setIsCurrentSubscription] = useState(null);

  const currentUser = useSelector(getUser);
  const paymentSetting = useSelector(getPaymentSettings);
  const stripeAttachSubscribeSuccess = useSelector(getStripeAttachSubscribeSuccess);
  const stripeStatusVal = checkSubscriptionStatus(currentUser);

  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const getXeroSubscription=useSelector(getXeroSubscriptionData);
  const paymentSettingRef = useRef(paymentSetting);
  paymentSettingRef.current = paymentSetting;
  const subscriptionFetchInFlightRef = useRef(false);
  const subscriptionRequestedForPathRef = useRef(false);

  const getCurrentSubscription = useCallback(async () => {
    if (subscriptionFetchInFlightRef.current) {
      return;
    }
    subscriptionFetchInFlightRef.current = true;
    const billing = paymentSettingRef.current;
    try {
      setIsFetchCurrentSubs(true);
      const response = await api.stripeCurrentSubscription();
      if (response) {
        if (
          response?.subscription?.current_plan &&
          billing?.xero_subscription_status !== "CANCELLED" &&
          billing?.xero_subscription_status !== "PAST_DUE"
        ) {
          const resData = JSON.parse(response?.subscription?.current_plan);
          const noOfParticipants = resData?.name?.split(" ");
          const priceAmount =
            resData?.subscriptionItems && resData?.subscriptionItems.length > 0
              ? resData?.subscriptionItems[0]?.price?.amount
              : "N/A";

          setIsCurrentSubscription({
            ...response?.subscription,
            noOfParticipants: `${noOfParticipants[2]} Participants`,
            duration: `${priceAmount} Plus GST/month `,
            subscriptionPlan: `${noOfParticipants[0]} ${noOfParticipants[1]}`,
            current_participant_count: response?.subscription?.current_participant_count,
          });
          return;
        }

        if (response?.subscription?.plan?.nickname) {
          const [participantsRange, pricingDetails] =
            response.subscription.plan.nickname.split("$");
          setIsCurrentSubscription({
            ...response?.subscription?.plan,
            noOfParticipants: participantsRange.trim(),
            duration: pricingDetails.trim(),
            subscriptionPlan: response?.subscription?.metadata?.product_name,
            current_participant_count: response?.subscription?.current_participant_count,
          });
          return;
        }

        if (
          response?.subscription?.status &&
          ["active", "trialing", "past_due"].includes(response.subscription.status)
        ) {
          setIsCurrentSubscription({
            id: response?.subscription?.id,
            subscriptionPlan:
              response?.subscription?.metadata?.product_name ||
              billing?.plan_features?.tier_label ||
              "Active Plan",
            noOfParticipants: "—",
            duration: response.subscription.status === "past_due" ? "Payment past due" : "Active",
            current_participant_count: response?.subscription?.current_participant_count,
          });
        }
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      subscriptionFetchInFlightRef.current = false;
      setIsFetchCurrentSubs(false);
    }
  }, []);

  useEffect(() => {
    subscriptionRequestedForPathRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (subscriptionRequestedForPathRef.current) {
      return;
    }
    subscriptionRequestedForPathRef.current = true;
    getCurrentSubscription();
  }, [location.pathname, getCurrentSubscription]);

  useEffect(() => {
    if (stripeAttachSubscribeSuccess) {
      getCurrentSubscription();
    }
  }, [stripeAttachSubscribeSuccess, getCurrentSubscription]);

  useEffect(() => {
    const refreshPlanDetails = () => {
      getCurrentSubscription();
    };

    window.addEventListener(
      STRIPE_SUBSCRIPTION_UPDATED_EVENT,
      refreshPlanDetails
    );
    return () => {
      window.removeEventListener(
        STRIPE_SUBSCRIPTION_UPDATED_EVENT,
        refreshPlanDetails
      );
    };
  }, [getCurrentSubscription]);

  /** Retry briefly when billing exists but plan details are not ready yet. */
  useEffect(() => {
    if (
      !paymentSetting?.subscription_id ||
      isCurrentSubscription !== null ||
      isFetchCurrentSubs
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      getCurrentSubscription();
    }, 3000);
    const stopId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(stopId);
    };
  }, [
    paymentSetting?.subscription_id,
    isCurrentSubscription,
    isFetchCurrentSubs,
    getCurrentSubscription,
  ]);

  const handleRetryPayment = async () => {
    try {
      if (isLoading === true) return;

      setIsLoading(true);
      const response = await api.stripeRetryPayment();

      setIsLoading(false);

      if (response) {
        toast.info(
          "Thank you for subscribing! Your subscription is currently being processed and will be updated shortly."
        );
        const user = await GetItemFromLocalstorage("user");
        if (user && user?.payment_setting) {
          const pay = user?.payment_setting;
          pay.errorMessage = "";
          user.payment_setting = pay;
        }
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(authenticatedUser(user));
      }
    } catch (err) {
      setIsLoading(false);
      console.log("err", err);
      // toast.error(err?.message ? err?.message : "Something Went Wrong!");
    }
  };

  

  const isErrorInPayment = () => {
    return (
      (stripeStatusVal === STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENTFAILED ||
        stripeStatusVal ===
          STRIPE_CHECK_SUBSCRIPTION_STATUS.PAYMENT_FAILED_TRAIL_EXISTS ||
        stripeStatusVal ===
          STRIPE_CHECK_SUBSCRIPTION_STATUS.RETRY_PAYMENT_FAILED ||
        stripeStatusVal ===
          STRIPE_CHECK_SUBSCRIPTION_STATUS.SUBSCRIPTION_UNPAID) &&
      paymentSetting?.errorMessage &&
      paymentSetting?.errorMessage?.length > 0
    );
  };

  /** Parse plan participant range (e.g. "11 to 20 Participants") to get max allowance. */
  const getParticipantAllowanceFromPlan = () => {
    const label = isCurrentSubscription?.noOfParticipants?.replace(":", "")?.trim();
    if (!label || typeof label !== "string") return null;
    if (label.includes("200+")) return { min: 200, max: 999, label };
    const match = label.match(/(\d+)\s*to\s*(\d+)/i);
    if (match) return { min: parseInt(match[1], 10), max: parseInt(match[2], 10), label };
    return { label };
  };

  const participantAllowance = getParticipantAllowanceFromPlan();
  const currentParticipantCount =
    Number(isCurrentSubscription?.current_participant_count) ??
    Number(paymentSetting?.current_participant_count) ??
    0;
  const maxParticipants = participantAllowance?.max ?? 0;
  const usagePercent = maxParticipants > 0 ? Math.min(100, Math.round((currentParticipantCount / maxParticipants) * 100)) : 0;
  const isNearOrOverLimit = maxParticipants > 0 && currentParticipantCount >= maxParticipants * 0.9;



  return (
    <div className='relative bg-indigo-200 p-4 sm:p-6 rounded-sm overflow-hidden mb-8 md:rounded-lg'>
      <div
        className='absolute right-0 top-0 -mt-4 mr-16 pointer-events-none hidden xl:block'
        aria-hidden='true'
      >
        <svg width='319' height='198' xmlnsXlink='http://www.w3.org/1999/xlink'>
          <defs>
            <path id='welcome-a' d='M64 0l64 128-64-20-64 20z' />
            <path id='welcome-e' d='M40 0l40 80-40-12.5L0 80z' />
            <path id='welcome-g' d='M40 0l40 80-40-12.5L0 80z' />
            <linearGradient x1='50%' y1='0%' x2='50%' y2='100%' id='welcome-b'>
              <stop stopColor='#A5B4FC' offset='0%' />
              <stop stopColor='#818CF8' offset='100%' />
            </linearGradient>
            <linearGradient
              x1='50%'
              y1='24.537%'
              x2='50%'
              y2='100%'
              id='welcome-c'
            >
              <stop stopColor='#4338CA' offset='0%' />
              <stop stopColor='#6366F1' stopOpacity='0' offset='100%' />
            </linearGradient>
          </defs>
          <g fill='none' fillRule='evenodd'>
            <g transform='rotate(64 36.592 105.604)'>
              <mask id='welcome-d' fill='#fff'>
                <use xlinkHref='#welcome-a' />
              </mask>
              <use fill='url(#welcome-b)' xlinkHref='#welcome-a' />
              <path
                fill='url(#welcome-c)'
                mask='url(#welcome-d)'
                d='M64-24h80v152H64z'
              />
            </g>
            <g transform='rotate(-51 91.324 -105.372)'>
              <mask id='welcome-f' fill='#fff'>
                <use xlinkHref='#welcome-e' />
              </mask>
              <use fill='url(#welcome-b)' xlinkHref='#welcome-e' />
              <path
                fill='url(#welcome-c)'
                mask='url(#welcome-f)'
                d='M40.333-15.147h50v95h-50z'
              />
            </g>
            <g transform='rotate(44 61.546 392.623)'>
              <mask id='welcome-h' fill='#fff'>
                <use xlinkHref='#welcome-g' />
              </mask>
              <use fill='url(#welcome-b)' xlinkHref='#welcome-g' />
              <path
                fill='url(#welcome-c)'
                mask='url(#welcome-h)'
                d='M40.333-15.147h50v95h-50z'
              />
            </g>
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className='relative'>
        {isFetchCurrentSubs ? (
          <Loader isSmallView={true} cssClass={"text-black"} />
        ) : isCurrentSubscription === null ? (
          <div className='flex items-center justify-start gap-x-2'>
            <span>No Subscription Subscribe!</span>
            <p
              onClick={() => {
                navigate("/subscription/plans");
              }}
              className='w-auto flex justify-start items-center text-center gap-x-1 text-md text-indigo-500 hover:underline cursor-pointer'
            >
              Choose Plan
              <span className='inline-flex text-base'>
                <BiExit />
              </span>
            </p>
          </div>
        ) : (
          <>
            <div className='space-y-6'>
              {/* Plan summary cards - single column so long text (e.g. Billing) has room and never overflows */}
              <div className='space-y-4 max-w-md'>
                <div className='bg-white/90 backdrop-blur rounded-lg border border-indigo-100 px-4 py-3 shadow-sm'>
                  <p className='text-xs font-semibold uppercase tracking-wide text-indigo-600/90 mb-0.5'>Plan</p>
                  <p className='text-lg font-semibold text-slate-800'>{isCurrentSubscription?.subscriptionPlan ?? "—"}</p>
                </div>
                <div className='bg-white/90 backdrop-blur rounded-lg border border-indigo-100 px-4 py-3 shadow-sm'>
                  <p className='text-xs font-semibold uppercase tracking-wide text-indigo-600/90 mb-0.5'>{participantLabel} tier</p>
                  <p className='text-lg font-semibold text-slate-800'>
                    {applyParticipantTerminology(
                      isCurrentSubscription?.noOfParticipants?.replace(":", "")?.trim() ?? "—",
                      careSector
                    )}
                  </p>
                </div>
                <div className='bg-white/90 backdrop-blur rounded-lg border border-indigo-100 px-4 py-3 shadow-sm'>
                  <p className='text-xs font-semibold uppercase tracking-wide text-indigo-600/90 mb-0.5'>Billing</p>
                  <p className='text-lg font-semibold text-slate-800'>{isCurrentSubscription?.duration ? `$${isCurrentSubscription.duration}` : "—"}</p>
                </div>
              </div>

              {/* Participant/Consumer allowance */}
              {participantAllowance && (
                <div className='bg-white/95 backdrop-blur rounded-lg border border-indigo-100 px-4 py-4 shadow-sm'>
                  <div className='flex items-center gap-2 mb-3'>
                    <HiOutlineUserGroup className='h-5 w-5 text-indigo-600' aria-hidden='true' />
                    <h2 className='text-sm font-semibold text-slate-800'>{participantLabel} allowance</h2>
                  </div>
                  <p className='text-slate-600 text-sm mb-2'>
                    Your plan allows <strong className='text-slate-800'>up to {maxParticipants} {participantLabelPlural}</strong>.
                    {maxParticipants > 0 && (
                      <span className='ml-1'>
                        You're currently using <strong className={isNearOrOverLimit ? "text-amber-600" : "text-slate-800"}>{currentParticipantCount}</strong>.
                      </span>
                    )}
                  </p>
                  {maxParticipants > 0 && (
                    <div className='mt-2'>
                      <div className='flex justify-between text-xs text-slate-500 mb-1'>
                        <span>Usage</span>
                        <span>{currentParticipantCount} of {maxParticipants} {participantLabelPlural.toLowerCase()}</span>
                      </div>
                      <div className='h-2 w-full rounded-full bg-slate-200 overflow-hidden'>
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            isNearOrOverLimit ? "bg-amber-500" : "bg-indigo-500"
                          }`}
                          style={{ width: `${usagePercent}%` }}
                          role='progressbar'
                          aria-valuenow={currentParticipantCount}
                          aria-valuemin={0}
                          aria-valuemax={maxParticipants}
                          aria-label={`${participantLabel} usage`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CTA */}
              <div className='pt-2'>
                <button
                  type='button'
                  onClick={() => navigate("/subscription/plans")}
                  className='inline-flex items-center gap-x-2 text-indigo-600 hover:text-indigo-800 hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded'
                >
                  View or change subscription plans
                  <BiExit className='h-4 w-4' aria-hidden='true' />
                </button>
              </div>
              {paymentSetting?.xero_subscription_id !== null &&
                paymentSetting?.xero_subscription_status !== "ACTIVE" && (
                  <div>
                    <button
                      type='button'
                      onClick={() => navigate("/subscription/plans")}
                      className='text-sm text-indigo-500 hover:underline cursor-pointer'
                    >
                      More Details
                      <BiExit className='inline h-3 w-3 ml-0.5' />
                    </button>
                  </div>
                )}
            </div>
            {isErrorInPayment() && (
              <div className='py-3'>
                <hr className='line-hr-stripe-tab' />
              </div>
            )}
            {isErrorInPayment() ? (
              <div className='grid grid-cols-1 gap-x-2'>
                {defaultPaymentMethodLoader ? (
                  <Loader isSmallView={true} cssClass={"text-black"} />
                ) : (
                  <>
                    <div className='col-span-1'>
                      <h1 className='text-xl text-slate-800 font-bold mb-1  text-left'>
                        Payment Failure Reason:
                      </h1>
                    </div>
                    <div className='col-span-1 flex items-center gap-x-2'>
                      <span
                        style={{
                          color: "red",
                        }}
                        className='text-xl'
                      >
                        {paymentSetting?.errorMessage}
                      </span>
                      {isLoading ? (
                        <Loader isSmallView={true} cssClass={"text-black"} />
                      ) : (
                        <button
                          onClick={() => {
                            handleRetryPayment();
                          }}
                          className='w-auto flex justify-start items-center text-center gap-x-1 text-md text-indigo-500 hover:underline cursor-pointer'
                        >
                          Retry Payment
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetails;
